import React, { useState, useEffect, useCallback } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useLocation } from 'react-router-dom';
import { locationHubService } from '../../services/locationHubService';
import companyService from '../../services/CompanyService';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert';
import { MapPin, ArrowLeft } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import StateSelectField from '../common/StateSelectField.js';
import { hasInternalHubDispatchControls } from '../../utils/networkMode.js';
import HubGpsFields, { fillCurrentGps } from './HubGpsFields.js';
import DestinationLocationHubField from './DestinationLocationHubField.js';

const AddLocationHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialDestinationType = location.state?.destinationType === 'hub_destination'
    ? 'hub_destination'
    : 'branch_destination';
  const { user } = useAuth();
  const isSystemAdmin = user?.role === 'System Admin';
  const showDispatchControls = isSystemAdmin || hasInternalHubDispatchControls(user);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [parentHubOptions, setParentHubOptions] = useState([]);
  const [destinationHubOptions, setDestinationHubOptions] = useState([]);
  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    company_id:             (v) => isSystemAdmin ? validators.required(v, 'Company') : '',
    address:                (v) => validators.required(v, 'Address'),
    location:               (v) => validators.required(v, 'Hub/Branch'),
    state:                  (v) => validators.required(v, 'State'),
    location_code:          (v) => validators.required(v, 'Hub/Branch code'),
    location_hub_contact:   (v) => v ? validators.contactVarchar(v) : '',
    commission_percentage:  (v) => v ? validators.number(v, { min: 0, max: 100, label: 'Commission' }) : '',
    rate_per_kg:            (v) => v ? validators.number(v, { min: 0, label: 'Rate / KG' }) : '',
    monthly_revenue_target: (v) => v ? validators.number(v, { min: 0, label: 'Monthly revenue target' }) : '',
    parent_hub_id:          () => '',
    dispatch_types:         (v) => showDispatchControls && (!Array.isArray(v) || v.length === 0)
      ? 'Select at least one dispatch type'
      : '',
  };

  const [formData, setFormData] = useState({
    company_id: '',
    address: '',
    location: '',
    state: '',
    location_code: '',
    destination_type: initialDestinationType,
    parent_hub_id: '',
    location_hub_contact: '',
    commission_percentage: '',
    rate_per_kg: '',
    monthly_revenue_target: '',
    ownership_type: 'OWN',
    dispatch_types: ['OGCL'],
    allow_paid: true,
    allow_booking: true,
    allow_to_pay: true,
    allow_on_account: true,
    allow_delivery: true,
    latitude: '',
    longitude: '',
    destination_location_hub_ids: [],
  });

  const isBranch = formData.destination_type === 'branch_destination';

  const loadParentHubs = useCallback(async (companyId) => {
    try {
      const filters = { destination_type: 'hub_destination' };
      if (companyId) filters.company_id = companyId;
      const response = await locationHubService.getHubsForSelect(filters);
      const hubs = response.data.hubs || response.data.data || [];
      setParentHubOptions(hubs);
    } catch (error) {
      console.error('Error loading parent hubs:', error);
      setParentHubOptions([]);
    }
  }, []);

  const loadDestinationHubs = useCallback(async (companyId) => {
    try {
      const filters = {};
      if (companyId) filters.company_id = companyId;
      const response = await locationHubService.getHubsForSelect(filters);
      const hubs = response.data.hubs || response.data.data || [];
      setDestinationHubOptions(hubs);
      return hubs;
    } catch (error) {
      console.error('Error loading destination hubs:', error);
      setDestinationHubOptions([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (isSystemAdmin) {
      companyService.getActiveCompanies()
        .then((res) => setCompanies(res.data || []))
        .catch(() => Alert.error('Failed to load companies'));
    } else if (user?.company_internal_id) {
      setFormData((prev) => ({
        ...prev,
        company_id: String(user.company_internal_id),
      }));
    }
  }, [isSystemAdmin, user?.company_internal_id]);

  useEffect(() => {
    if (!isBranch) {
      setParentHubOptions([]);
      return;
    }
    const companyId = isSystemAdmin
      ? (formData.company_id || null)
      : (user?.company_internal_id || null);
    if (isSystemAdmin && !companyId) {
      setParentHubOptions([]);
      return;
    }
    loadParentHubs(companyId);
  }, [isBranch, formData.company_id, isSystemAdmin, user?.company_internal_id, loadParentHubs]);

  useEffect(() => {
    const companyId = isSystemAdmin
      ? (formData.company_id || null)
      : (user?.company_internal_id || null);
    if (isSystemAdmin && !companyId) {
      setDestinationHubOptions([]);
      setFormData((prev) => ({ ...prev, destination_location_hub_ids: [] }));
      return;
    }
    let cancelled = false;
    loadDestinationHubs(companyId).then((hubs) => {
      if (cancelled) return;
      setFormData((prev) => ({
        ...prev,
        destination_location_hub_ids: hubs.map((hub) => hub.id),
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [formData.company_id, isSystemAdmin, user?.company_internal_id, loadDestinationHubs]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'location_hub_contact') filtered = inputFilters.phoneVarchar(value);
    if (name === 'commission_percentage') filtered = inputFilters.amount(value);
    if (name === 'rate_per_kg') filtered = inputFilters.amount(value);
    if (name === 'monthly_revenue_target') filtered = inputFilters.amount(value);
    if (name === 'location' || name === 'location_code') filtered = value.toUpperCase();

    setFormData((prev) => {
      const next = { ...prev, [name]: filtered };
      if (name === 'destination_type' && filtered !== 'branch_destination') {
        next.parent_hub_id = '';
      }
      if (name === 'company_id') {
        next.parent_hub_id = '';
        next.destination_location_hub_ids = [];
      }
      if (name === 'ownership_type' && filtered === 'AGENT') {
        next.allow_on_account = false;
      }
      return next;
    });

    if (validationRules[name]) {
      const nextAll = {
        ...formData,
        [name]: filtered,
        ...(name === 'destination_type' && filtered !== 'branch_destination'
          ? { parent_hub_id: '' }
          : {}),
      };
      validateField(name, filtered, (v) => validationRules[name](v, nextAll));
    }
  };

  const handleDispatchTypeChange = (type) => {
    setFormData((prev) => {
      const dispatch_types = prev.dispatch_types.includes(type)
        ? prev.dispatch_types.filter((item) => item !== type)
        : [...prev.dispatch_types, type];
      validateField('dispatch_types', dispatch_types, validationRules.dispatch_types);
      return { ...prev, dispatch_types };
    });
  };

  const handleHubToggle = (name) => {
    setFormData((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, {
      ...validationRules,
      parent_hub_id: (v) => validationRules.parent_hub_id(v, formData),
    })) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    try {
      setLoading(true);
      
      const submitData = {
        address: formData.address.trim(),
        location: formData.location.trim().toUpperCase(),
        state: formData.state,
        location_code: formData.location_code.trim().toUpperCase(),
        destination_type: formData.destination_type,
        parent_hub_id: isBranch && formData.parent_hub_id
          ? parseInt(formData.parent_hub_id, 10)
          : null,
        destination_location_hub_ids: (formData.destination_location_hub_ids || [])
          .map((id) => parseInt(id, 10))
          .filter((id) => Number.isInteger(id)),
        location_hub_contact: formData.location_hub_contact
          ? String(formData.location_hub_contact).trim()
          : null,
        commission_percentage: formData.commission_percentage !== '' ? parseInt(formData.commission_percentage) : null,
        rate_per_kg: formData.rate_per_kg !== '' ? parseFloat(formData.rate_per_kg) : null,
        monthly_revenue_target: formData.monthly_revenue_target !== '' ? parseFloat(formData.monthly_revenue_target) : null,
        ...(showDispatchControls ? {
          ownership_type: formData.ownership_type,
          dispatch_types: formData.dispatch_types,
          allow_paid: formData.allow_paid,
          allow_to_pay: formData.allow_to_pay,
          allow_on_account: formData.allow_on_account,
          allow_delivery: formData.allow_delivery,
          allow_booking: formData.allow_booking,
        } : {}),
        latitude: formData.latitude === '' ? null : formData.latitude,
        longitude: formData.longitude === '' ? null : formData.longitude,
      };

      await locationHubService.createHub(
        submitData,
        isSystemAdmin ? parseInt(formData.company_id, 10) : null,
      );
      
      Alert.success('Location hub created successfully!');
      navigate('/location-hubs');
      
    } catch (error) {
      console.error('Error creating hub:', error);
      Alert.error(error.response?.data?.error || 'Failed to create location hub');
    } finally {
      setLoading(false);
    }
  };

  const selectedParent = parentHubOptions.find(
    (hub) => String(hub.id) === String(formData.parent_hub_id)
  );

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3">
                    <div className='flex w-full'>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                        <MapPin className="w-6 h-6 text-secondary" />
                        {isBranch ? 'Add Branch' : 'Add Hub'}
                      </h1>
                    </div>
                    <div className="flex justify-end w-full">
                      <div className="flex items-center gap-3 sm:gap-4 md:gap-6">
                        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/location-hubs')}>
                          Back to Location Hubs
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Hub Information
                    </h3>
                  </div>

                  <div className='grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6'>
                    {isSystemAdmin && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Company <span className="text-red-500">*</span>
                        </label>
                        <SelectField variant="form"
                          name="company_id"
                          value={formData.company_id}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.company_id ? 'border-red-400' : 'border-gray-300'}`}
                          required
                          disabled={loading}
                        >
                          <option value="">Select company</option>
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.company_name} ({company.company_id})
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.company_id} />
                      </div>
                    )}

                    {/* Destination */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Hub/Branch <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Enter destination name"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.location ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.location} />
                    </div>

                    {/* Destination Code / Location Code */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Location Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="location_code"
                        value={formData.location_code}
                        onChange={handleChange}
                        placeholder="e.g., MU for Mumbai"
                        maxLength="10"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base uppercase ${errors.location_code ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.location_code} />
                    </div>

                    {/* Destination Type */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Destination Type <span className="text-red-500">*</span>
                      </label>
                      <SelectField variant="form"
                        name="destination_type"
                        value={formData.destination_type}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        disabled={loading}
                      >
                        <option value="branch_destination">Branch</option>
                        <option value="hub_destination">Hub</option>
                      </SelectField>
                    </div>

                    {/* Parent Hub — only for Branch */}
                    {isBranch && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Parent Hub <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <SelectField variant="form"
                          name="parent_hub_id"
                          value={formData.parent_hub_id}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.parent_hub_id ? 'border-red-400' : 'border-gray-300'}`}
                          disabled={loading || (isSystemAdmin && !formData.company_id)}
                        >
                          <option value="">
                            {isSystemAdmin && !formData.company_id
                              ? 'Select company first'
                              : parentHubOptions.length === 0
                                ? 'No hubs available'
                                : 'Select parent hub'}
                          </option>
                          {parentHubOptions.map((hub) => (
                            <option key={hub.id} value={hub.id}>
                              {hub.location} ({hub.location_code})
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.parent_hub_id} />
                        {selectedParent && (
                          <p className="mt-1 text-xs text-gray-500">
                            Parent location code: <span className="font-mono font-semibold">{selectedParent.location_code}</span>
                          </p>
                        )}
                      </div>
                    )}

                    <DestinationLocationHubField
                      hubs={destinationHubOptions}
                      selectedIds={formData.destination_location_hub_ids || []}
                      onChange={(ids) => setFormData((prev) => ({
                        ...prev,
                        destination_location_hub_ids: ids,
                      }))}
                      sourceLabel={formData.location.trim() || 'This location hub'}
                      sourceCode={formData.location_code.trim()}
                      disabled={loading || (isSystemAdmin && !formData.company_id)}
                    />

                    {/* Commission Percentage */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Commission %
                      </label>
                      <input
                        type="text"
                        name="commission_percentage"
                        value={formData.commission_percentage}
                        onChange={handleChange}
                        placeholder="0 - 100"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.commission_percentage ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.commission_percentage} />
                    </div>

                    {/* Rate / KG */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Rate / KG (₹) <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        name="rate_per_kg"
                        value={formData.rate_per_kg}
                        onChange={handleChange}
                        placeholder="e.g. 4.5"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.rate_per_kg ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <p className="mt-1 text-xs text-gray-400">Used for weight-based freight when this hub is the receiver</p>
                      <ValidationMessage error={errors.rate_per_kg} />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Monthly revenue target (₹) <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        name="monthly_revenue_target"
                        value={formData.monthly_revenue_target}
                        onChange={handleChange}
                        placeholder="e.g. 500000"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.monthly_revenue_target ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <p className="mt-1 text-xs text-gray-400">Compared with revenue booked from this location</p>
                      <ValidationMessage error={errors.monthly_revenue_target} />
                    </div>

                    {/* Address */}
                    <div className="col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter complete address"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.address ? 'border-red-400' : 'border-gray-300'}`}
                        required
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.address} />
                    </div>

                    <HubGpsFields
                      formData={formData}
                      onChange={handleChange}
                      loading={loading}
                      onUseCurrent={() => fillCurrentGps((coords) => setFormData((prev) => ({ ...prev, ...coords })))}
                    />

                    {showDispatchControls && (
                      <div className="col-span-2 sm:col-span-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <h4 className="font-semibold text-secondary mb-3">Hub Dispatch Capabilities</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">Ownership Type</label>
                            <SelectField variant="form" name="ownership_type" value={formData.ownership_type} onChange={handleChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded-xl">
                              <option value="OWN">Own</option>
                              <option value="AGENT">Agent</option>
                            </SelectField>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="text-xs sm:text-sm font-medium text-secondary mb-2">Dispatch Types <span className="text-red-500">*</span></p>
                            <div className="flex gap-5">
                              {['OGCL', 'ITCL'].map((type) => (
                                <label key={type} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={formData.dispatch_types.includes(type)} onChange={() => handleDispatchTypeChange(type)} disabled={loading} />
                                  {type}
                                </label>
                              ))}
                            </div>
                            <ValidationMessage error={errors.dispatch_types} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
                          {[
                            ['allow_paid', 'Allow Paid'],
                            ['allow_to_pay', 'Allow To Pay'],
                            ['allow_on_account', 'Allow On Account'],
                            ['allow_delivery', 'Allow Delivery'],
                            ['allow_booking', 'Allow Booking'],
                          ].map(([name, label]) => (
                            <label key={name} className="flex items-center gap-2 text-sm text-gray-700">
                              <input type="checkbox" checked={formData[name]} onChange={() => handleHubToggle(name)} disabled={loading} />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <StateSelectField
                      label="State"
                      required
                      disabled={loading}
                      value={formData.state}
                      error={errors.state}
                      onChange={(state) => {
                        setFormData({ ...formData, state });
                        validateField('state', state, validationRules.state);
                      }}
                    />

                    {/* Contact */}
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Contact No.
                      </label>
                      <input
                        type="text"
                        name="location_hub_contact"
                        value={formData.location_hub_contact}
                        onChange={handleChange}
                        placeholder="Enter contact number"
                        className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.location_hub_contact ? 'border-red-400' : 'border-gray-300'}`}
                        disabled={loading}
                      />
                      <ValidationMessage error={errors.location_hub_contact} />
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" variant="primary" disabled={loading} loading={loading}>
                      {loading ? 'Creating...' : 'Create Hub'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/location-hubs')} disabled={loading}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default AddLocationHub;
